import React, { useState, useEffect, useRef } from 'react';
import { Component } from '@/types';

interface SharedModalComponentProps {
  component: Component;
  children?: React.ReactNode;
  isViewerMode?: boolean;
}

const SIZE_MAP: Record<string, string> = {
  sm: '400px',
  md: '600px',
  lg: '800px',
  full: '100%',
};

function getTriggerButtonStyle(triggerVariant: string, cursor: React.CSSProperties['cursor'] = 'pointer'): React.CSSProperties {
  const primary = 'var(--color-primary, #4CAF50)';
  const secondary = 'var(--color-secondary, #2196F3)';

  return {
    padding: cursor === 'default' ? '6px 16px' : '8px 20px',
    borderRadius: '6px',
    fontSize: cursor === 'default' ? '13px' : '14px',
    fontWeight: 500,
    cursor,
    border: triggerVariant === 'outline' || triggerVariant === 'ghost'
      ? `1px solid ${primary}`
      : '1px solid transparent',
    backgroundColor: triggerVariant === 'primary' ? primary
      : triggerVariant === 'secondary' ? secondary
      : 'transparent',
    color: triggerVariant === 'primary' || triggerVariant === 'secondary'
      ? '#ffffff'
      : primary,
  };
}

export const SharedModalComponent: React.FC<SharedModalComponentProps> = ({
  component,
  children,
  isViewerMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  const props = component.properties || {};
  const title = (props.title as string) || 'Modal';
  const triggerText = (props.triggerText as string) || 'Open Modal';
  const triggerVariant = (props.triggerVariant as string) || 'primary';
  const size = (props.size as string) || 'md';
  const showBackdrop = props.showBackdrop !== false;
  const closeOnBackdropClick = props.closeOnBackdropClick !== false;
  const showCloseButton = props.showCloseButton !== false;

  if (!isViewerMode) {
    // ── Editor Preview Mode ──
    return (
      <div
        data-testid="modal-editor-preview"
        style={{
          width: '100%',
          minHeight: '120px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#fafafa',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* Trigger button preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            style={getTriggerButtonStyle(triggerVariant, 'default')}
          >
            {triggerText}
          </button>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            Click trigger in viewer mode
          </span>
        </div>

        {/* Modal content preview area */}
        <div
          style={{
            border: '2px dashed #d1d5db',
            borderRadius: '6px',
            padding: '12px',
            minHeight: '60px',
            backgroundColor: '#ffffff',
          }}
        >
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#6b7280',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <span>{title}</span>
            <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: 'auto' }}>
              {size}
            </span>
          </div>
          <div>{children}</div>
        </div>
      </div>
    );
  }

  // ── Viewer Interactive Mode ──
  const maxWidth = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <>
      <button
        data-testid="modal-trigger"
        onClick={() => setIsOpen(true)}
        style={getTriggerButtonStyle(triggerVariant)}
      >
        {triggerText}
      </button>

      {isOpen && (
        <div
          data-testid="modal-overlay"
          onClick={closeOnBackdropClick ? () => setIsOpen(false) : undefined}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: showBackdrop ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
          }}
        >
          <div
            ref={dialogRef}
            data-testid="modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth,
              width: size === 'full' ? '100%' : '90%',
              maxHeight: '90vh',
              backgroundColor: '#ffffff',
              borderRadius: size === 'full' ? '0' : '12px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <h2 id="modal-title" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                {title}
              </h2>
              {showCloseButton && (
                <button
                  data-testid="modal-close"
                  aria-label="Close modal"
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Body */}
            <div
              style={{
                padding: '20px',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {children}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SharedModalComponent;
